import { useEffect } from 'react'

/**
 * REQ-PWA-005 — Deshabilita zoom y gestos de pellizco SOLO en modo standalone.
 *
 * Implementación (ver §3.5 SRS):
 *   1. Reescribe el meta viewport con user-scalable=no, maximum-scale=1.
 *      Android Chrome respeta esto de forma nativa.
 *   2. Para iOS Safari (que ignora user-scalable=no parcialmente), añade listeners
 *      en `document` para prevenir:
 *        - gesturestart / gesturechange / gestureend
 *        - touchstart con e.touches.length > 1 (pellizco multi-touch)
 *   3. En navegador web tradicional NO se aplica: el zoom queda habilitado por accesibilidad.
 *
 * El llamador (typically AppShell) pasa `enabled` para activar/desactivar.
 */
export function useZoomDisabler(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return

    // 1. Reescribir meta viewport
    const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]')
    const previousContent = meta?.getAttribute('content') ?? null
    if (meta) {
      meta.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no, viewport-fit=cover',
      )
    }

    // 2. Listeners para iOS Safari
    const preventGesture = (e: Event) => e.preventDefault()
    const preventMultiTouch = (e: TouchEvent) => {
      if (e.touches.length > 1) e.preventDefault()
    }

    document.addEventListener('gesturestart', preventGesture, { passive: false })
    document.addEventListener('gesturechange', preventGesture, { passive: false })
    document.addEventListener('gestureend', preventGesture, { passive: false })
    document.addEventListener('touchstart', preventMultiTouch, { passive: false })

    return () => {
      // Restaurar viewport original al desactivar
      if (meta && previousContent !== null) {
        meta.setAttribute('content', previousContent)
      }
      document.removeEventListener('gesturestart', preventGesture)
      document.removeEventListener('gesturechange', preventGesture)
      document.removeEventListener('gestureend', preventGesture)
      document.removeEventListener('touchstart', preventMultiTouch)
    }
  }, [enabled])
}
